/**
 * Zone surface-area calculator — a faithful port of the `Surface Area
 * Calculator` tab in the SME's "NII Master File" workbook, which until now was
 * the only place a zone's area could be produced. The SME kept the workbook
 * open beside the app, typed the diameter and length in, read the result off
 * cell F25, and typed that number back into the zone card.
 *
 * The area is NOT a judgement call — it is arithmetic, and it feeds `F_ZONE`
 * and the required coverage area that reach the client deliverable. So it lives
 * here as tested code, and only its INPUTS are sourced (from APM, the GA
 * drawing, or the AI prefill). `zoneSurfaceArea.test.ts` pins every formula to
 * the workbook's own saved cell values.
 *
 * Cell provenance (NII Master File v4, sheet "Surface Area Calculator"):
 *   F3  cylinder      =PI()*F4*F5/1000000
 *   F11 ellipsoidal   =IF(H5="Tan to Tan", (1.083985338*F10^2)/1000000,
 *                          (PI()*F4*(0.03*F4)/1000000 + (1.083985338*F10^2)/1000000))
 *   F16 hemispherical =(2)*PI()*((F15/2)^2)/1000000
 *   F25 total         =IF(H23="ellipsoidal", F3*F23+F24*F11, F3*F23+F24*F16)
 *
 * This module is a LEAF: it depends on nothing in the feature, so the zone
 * shape (`zoneAnalysisApi`) can import it without a cycle.
 */

/**
 * Surface-area coefficient for a 2:1 ellipsoidal head, taken verbatim from the
 * workbook rather than re-derived — the whole point of this port is that an SME
 * can reconcile a zone against their own spreadsheet digit for digit.
 */
const ELLIPSOIDAL_HEAD_COEFFICIENT = 1.083985338

/**
 * A dished head's straight flange, as a fraction of diameter. The weld seam
 * sits inboard of the tangent line, with the straight flange between them, so a
 * SEAM-to-seam length stops short of the tangent line and the flange band is
 * missing from `pi * D * L`; this adds it back, per head. A TAN-to-tan length
 * already spans both flanges and needs no correction.
 */
const STRAIGHT_FLANGE_DIAMETER_FRACTION = 0.03

/** mm² per m² — the workbook's `/1000000`. */
const SQ_MM_PER_SQ_M = 1_000_000

/**
 * Decimal places of the PERSISTED final area. The breakdown stays at full
 * float precision so the workbook contract tests can reconcile digit for
 * digit; only the value written onto the zone — the one the card displays and
 * coverage consumes — is cut to the precision the SME actually reads.
 */
const PERSISTED_AREA_DECIMALS = 2

function roundToPersistedPrecision(areaSqM: number): number {
  const factor = 10 ** PERSISTED_AREA_DECIMALS
  return Math.round(areaSqM * factor) / factor
}

// ── Option sets (also the UI selects' single source of truth) ──────────

export const HEAD_TYPE_OPTIONS = [
  { value: 'ELLIPSOIDAL', label: 'Ellipsoidal (2:1)' },
  { value: 'HEMISPHERICAL', label: 'Hemispherical' },
] as const

export const LENGTH_BASIS_OPTIONS = [
  { value: 'TAN_TO_TAN', label: 'Tangent to tangent' },
  { value: 'SEAM_TO_SEAM', label: 'Seam to seam' },
] as const

export type ZoneHeadType = (typeof HEAD_TYPE_OPTIONS)[number]['value']
export type ZoneLengthBasis = (typeof LENGTH_BASIS_OPTIONS)[number]['value']

/**
 * Defaults for the two dropdowns, applied when a zone has never been through
 * the calculator. Both follow the SME: heads are ellipsoidal "95% of the time",
 * and GA drawings quote a tangent-line length ("between tangent lines" on the
 * sample vessel). Deliberately NOT the workbook's own fallthrough — its
 * `IF(H5="Tan to Tan", …)` sends every other value, including a blank cell, down
 * the seam-to-seam branch and silently adds flange area.
 */
export const DEFAULT_HEAD_TYPE: ZoneHeadType = 'ELLIPSOIDAL'
export const DEFAULT_LENGTH_BASIS: ZoneLengthBasis = 'TAN_TO_TAN'
/** A length typed straight into the zone card IS the zone's length. */
export const DEFAULT_CYLINDER_PROPORTION = 1

// ── Inputs / outputs ───────────────────────────────────────────────────

/**
 * The workbook's six inputs. Structural rather than `ZoneParams` so this stays
 * a leaf; the full zone is assignable, and `readZoneSurfaceAreaInputs` maps a
 * zone onto it (diameter and length come from fields the card already had).
 */
export interface ZoneSurfaceAreaInputs {
  /** F4/F10/F15 — OUTER diameter, mm. Drawings give ID: OD = ID + 2 x wall. */
  outerDiameterMm?: number
  /** F5 — length, mm, on the basis named below. */
  lengthMm?: number
  /** H5 — which length the drawing gave. */
  lengthBasis?: ZoneLengthBasis
  /** F23 — proportion of that cylinder inside this zone, 0-1. */
  cylinderProportion?: number
  /** H23 — head geometry. */
  headType?: ZoneHeadType
  /** F24 — whole-head equivalents in this zone (2 heads → 2, half a head → 0.5). */
  headProportion?: number
}

export interface ZoneSurfaceAreaBreakdown {
  /** F3 — the WHOLE cylinder, before the zone's proportion of it. */
  cylinderAreaSqM: number
  /** F11 or F16 — ONE whole head of the selected type. */
  headAreaSqM: number
  /** This zone's share of the cylinder. */
  cylinderContributionSqM: number
  /** This zone's share of the heads. */
  headContributionSqM: number
  /** F25 — the number that feeds F_ZONE and the required coverage area. */
  totalSqM: number
}

/** F3 — lateral area of a cylinder. */
function cylinderArea(outerDiameterMm: number, lengthMm: number): number {
  return (Math.PI * outerDiameterMm * lengthMm) / SQ_MM_PER_SQ_M
}

/** F11 — one whole 2:1 ellipsoidal head, plus the straight flange when the
 * length quoted was seam-to-seam. */
function ellipsoidalHeadArea(
  outerDiameterMm: number,
  lengthBasis: ZoneLengthBasis
): number {
  const crown =
    (ELLIPSOIDAL_HEAD_COEFFICIENT * outerDiameterMm ** 2) / SQ_MM_PER_SQ_M
  if (lengthBasis === 'TAN_TO_TAN') return crown
  const straightFlange =
    (Math.PI *
      outerDiameterMm *
      (STRAIGHT_FLANGE_DIAMETER_FRACTION * outerDiameterMm)) /
    SQ_MM_PER_SQ_M
  return straightFlange + crown
}

/** F16 — one whole hemispherical head. */
function hemisphericalHeadArea(outerDiameterMm: number): number {
  return (2 * Math.PI * (outerDiameterMm / 2) ** 2) / SQ_MM_PER_SQ_M
}

/**
 * F25 — the zone's total surface area in m², or `undefined` when it cannot be
 * computed. Diameter is the one hard requirement; everything else has a
 * documented default so a half-filled zone still produces the honest partial
 * answer instead of nothing.
 *
 * A zone made only of heads (an exchanger's channel/tube ends) is a real case,
 * so a missing length is treated as no cylinder rather than as invalid. Heads
 * default to NONE: a mid-shell band is the common zone, and inventing head area
 * would inflate the deliverable. Nonsense input (negative, NaN, Infinity) is
 * rejected outright rather than clamped — a silently corrected diameter would
 * hand back a plausible-looking area built on a number the SME never entered.
 */
export function calculateZoneSurfaceArea(
  inputs: ZoneSurfaceAreaInputs
): ZoneSurfaceAreaBreakdown | undefined {
  const {
    outerDiameterMm,
    lengthMm = 0,
    lengthBasis = DEFAULT_LENGTH_BASIS,
    cylinderProportion = DEFAULT_CYLINDER_PROPORTION,
    headType = DEFAULT_HEAD_TYPE,
    headProportion = 0,
  } = inputs

  const measures = [
    outerDiameterMm,
    lengthMm,
    cylinderProportion,
    headProportion,
  ]
  if (measures.some((n) => n === undefined || !Number.isFinite(n) || n < 0)) {
    return undefined
  }
  if (!outerDiameterMm) return undefined

  const cylinderAreaSqM = cylinderArea(outerDiameterMm, lengthMm)
  const headAreaSqM =
    headType === 'HEMISPHERICAL'
      ? hemisphericalHeadArea(outerDiameterMm)
      : ellipsoidalHeadArea(outerDiameterMm, lengthBasis)

  const cylinderContributionSqM = cylinderAreaSqM * cylinderProportion
  const headContributionSqM = headAreaSqM * headProportion

  return {
    cylinderAreaSqM,
    headAreaSqM,
    cylinderContributionSqM,
    headContributionSqM,
    totalSqM: cylinderContributionSqM + headContributionSqM,
  }
}

// ── Zone binding ───────────────────────────────────────────────────────

/**
 * Structural subset of `ZoneParams` the calculator reads. Diameter and length
 * are the fields the Design & Function panel ALREADY collected — the calculator
 * adds only the four that had nowhere to live.
 */
export type SurfaceAreaZone = {
  external_diameter_mm?: number
  height_mm?: number
  height_mm_override?: number
  calculated_height_mm?: number
  length_basis?: string
  cylinder_proportion?: number
  head_type?: string
  head_proportion?: number
}

/**
 * Zone fields that change the calculated area. Callers use this to decide
 * whether an edit needs a recompute, so an unrelated field (material, design
 * pressure) does not rewrite the area and the coverage that follows from it.
 */
export const SURFACE_AREA_INPUT_FIELDS = [
  'external_diameter_mm',
  'height_mm',
  'height_mm_override',
  'calculated_height_mm',
  'length_basis',
  'cylinder_proportion',
  'head_type',
  'head_proportion',
] as const

export function affectsZoneSurfaceArea(updates: object): boolean {
  return SURFACE_AREA_INPUT_FIELDS.some((field) => field in updates)
}

/** Narrow a persisted string to a known option, falling back to the default. */
function asOption<T extends string>(
  raw: string | undefined,
  options: readonly { value: T }[],
  fallback: T
): T {
  const match = options.find((option) => option.value === raw)
  return match ? match.value : fallback
}

/**
 * Map a zone onto the calculator's inputs. The length is the zone's effective
 * height — the same `override ?? value ?? calculated` cascade the card's "Zone
 * Height or Length" field displays, so what the SME reads is what is computed.
 */
export function readZoneSurfaceAreaInputs(
  zone: SurfaceAreaZone
): ZoneSurfaceAreaInputs {
  return {
    outerDiameterMm: zone.external_diameter_mm,
    lengthMm:
      zone.height_mm_override ?? zone.height_mm ?? zone.calculated_height_mm,
    lengthBasis: asOption(
      zone.length_basis,
      LENGTH_BASIS_OPTIONS,
      DEFAULT_LENGTH_BASIS
    ),
    cylinderProportion: zone.cylinder_proportion ?? DEFAULT_CYLINDER_PROPORTION,
    headType: asOption(zone.head_type, HEAD_TYPE_OPTIONS, DEFAULT_HEAD_TYPE),
    headProportion: zone.head_proportion,
  }
}

export function calculateZoneSurfaceAreaFromZone(
  zone: SurfaceAreaZone
): ZoneSurfaceAreaBreakdown | undefined {
  return calculateZoneSurfaceArea(readZoneSurfaceAreaInputs(zone))
}

/**
 * The derivation to apply after any edit to a surface-area input: the patch
 * that puts the recomputed area on the zone.
 *
 * Returns `{}` — never `{surface_area_calculated_sq_m: undefined}` — when the
 * inputs do not yet produce an area. Clearing the diameter must not delete an
 * area the SME already reviewed; a stale-until-recomputable value is the lesser
 * failure, and the SME's own override outranks this field regardless.
 */
export function deriveZoneSurfaceArea(zone: SurfaceAreaZone): {
  surface_area_calculated_sq_m?: number
} {
  const breakdown = calculateZoneSurfaceAreaFromZone(zone)
  if (breakdown === undefined || breakdown.totalSqM <= 0) return {}
  return {
    surface_area_calculated_sq_m: roundToPersistedPrecision(breakdown.totalSqM),
  }
}
